import type { IntlShapeV2 } from '@atlassian/jira-intl/src/v2/types.tsx';

export type Intl = {
	// This is for internal use only
	intl: IntlShapeV2;
};

export type PropsWithIntl<TProps> = Intl & TProps;

export type FormatMessage = IntlShapeV2['formatMessage'];

export type Optional<T> = T | undefined;

export type Hash<T> = {
	[key: string]: T;
};

// You probably do not need to use RefObject. Consider importing { type Ref } from 'react' if you feel like you need that.
// Check out https://hello.atlassian.net/wiki/spaces/JPLAT/pages/472090048/How+to+type+React+references+ref+and+createRef for alternatives.
export type RefObject<T = HTMLElement> = {
	current: T | null;
};

export type Href = string;
export const toHref = (value: string): Href => value;

export type ProjectId = string;
export const toProjectId = (value: string): ProjectId => value;
export const toProjectIdArray = (ids: string[]): ProjectId[] => ids;

/**
 * @deprecated Use `ProjectId` or a project ARI via `@atlassian/jira-platform-ari` package's `createAri` instead.
 */
export type ProjectUuid = string;
/**
 * @deprecated `ProjectUuid` is deprecated. Use `ProjectId` or a project ARI
 *    via `@atlassian/jira-platform-ari` package's `createAri` instead.
 */
export const toProjectUuid = (value: string): ProjectUuid => value;

export type IssueTypeUuid = string;
export const toIssueTypeUuid = (value: string): IssueTypeUuid => value;

export type ProjectKey = string;
export const toProjectKey = (value: string): ProjectKey => value;

export type IssueId = string;
export const toIssueId = (value: string): IssueId => value;
export const toIssueIdArray = (ids: string[]): IssueId[] => ids;

export type SprintId = string;

export type IssueKey = string;
export const toIssueKey = (value: string): IssueKey => value;

export type IssueStatusId = string;
export const toIssueStatusId = (value: string): IssueStatusId => value;

export type IssueStatusCategoryId = string;
export const toIssueStatusCategoryId = (value: string): IssueStatusCategoryId => value;
export const toIssueStatusCategoryIdArray = (ids: string[]): IssueStatusCategoryId[] => ids;

export type IssueTypeId = string;
export const toIssueTypeId = (value: string): IssueTypeId => value;
export const toIssueTypeIdArray = (ids: string[]): IssueTypeId[] => ids;

export type VersionId = string;
export const toVersionId = (value: string): VersionId => value;
export const toVersionIdArray = (ids: string[]): VersionId[] => ids;

export type ComponentId = string;
export const toComponentId = (value: string): ComponentId => value;
export const toComponentIdArray = (ids: string[]): ComponentId[] => ids;

export type RequestTypeId = string;

export type ConfluencePageId = string;
export const toConfluencePageId = (value: string): ConfluencePageId => value;

export type ConfluenceWhiteboardId = string;
export const toConfluenceWhiteboardId = (value: string): ConfluenceWhiteboardId => value;

export type RemoteLinkGlobalId = string;
export const toRemoteLinkGlobalId = (value: string): RemoteLinkGlobalId => value;

export type FailedRemoteLinkId = string;
export const toFailedRemoteLinkId = (value: string): FailedRemoteLinkId => value;

export type WebLinkId = string;
export const toWebLinkId = (value: string): WebLinkId => value;

// e.g. https://www.another-tenant.com
export type UrlOrigin = string;
export const toUrlOrigin = (value: string): UrlOrigin => value;

export type CloudId = string;
export const toCloudId = (value: string): CloudId => value;

export type DevopsProjectSignupSource = string;
export const toDevopsProjectSignupSource = (
	value: string | null,
): DevopsProjectSignupSource | null => (value != null && value.length > 0 ? value : null);

export type ActivationId = string;
export const toActivationId = (value: string): ActivationId => value;

export type CloudName = string;
export const toCloudName = (value: string): CloudName => value;

export type Username = string;
export const toUsername = (value?: string | null): Username | null =>
	value != null && value.length > 0 ? value : null;

// Different to Username, shouldn't be null and intended to be the GDPR compliant name to display
export type DisplayName = string;
export const toDisplayName = (value: string): DisplayName => value;

export type AccountId = string;
export const toAccountId = (value: string): AccountId => value;
export const toAccountIdArray = (ids: string[]): AccountId[] => ids;

export type TimeZone = string;
export const toTimeZone = (value: string): TimeZone => value;

export type FieldUuid = string;
export const toFieldUuid = (value: string): FieldUuid => value;

export type GroupUuid = string;
export const toGroupUuid = (value: string): GroupUuid => value;

export type OptionUuid = string;
export const toOptionUuid = (value: string): OptionUuid => value;

export type Locale = string;
export const toLocale = (value: string): Locale => value;

// This is the type used in classic backlog to return sprints for backlog. e.g.: GH.BacklogModel.sprintModels
// Used ths type to retrieve data from legacy side of js
export type InsightsSprintModel = {
	sprintData: {
		id: number;
		name: string;
	};
};

export type SprintModel = {
	id: number;
	name: string | null;
};

// These have been added as an escape hatch for edge cases where we want to avoid creating a new string/array.
// Try to implement opaque types as extensively as possible before using these functions.
export const toString = (value: string): string => value;
export const toStringArray = (arrayValues: string[]): string[] => arrayValues;

/**
 * `Object.values` alias that does proper type-safety for flow.
 */
export function values<T>(object: T): T[keyof T][] {
	// @ts-expect-error - TS2769 - No overload matches this call.
	return Object.values(object);
}
